import React, { ReactNode } from 'react'
import { Flex, Tooltip, Typography } from 'antd'
import { CheckOutlined, InfoCircleOutlined, MinusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { formatPrice, TierPricing } from '../pricing'

// User limit at or above which a tier is presented as "unlimited" (Pro and up use a sentinel limit in the backend).
const UNLIMITED_USER_THRESHOLD = 999

export type PlanFeature =
    | { kind: 'feature'; content: ReactNode }
    | { kind: 'missing'; content: ReactNode }
    | { kind: 'heading'; content: string }
    | { kind: 'note'; content: ReactNode }
    // Holds a row open, so the sections line up with plans that have a line there.
    | { kind: 'empty' }

function Info({ title }: { title: string }) {
    return (
        <Tooltip title={title}>
            <InfoCircleOutlined className="ml-1.5 text-xs text-ink-tertiary" />
        </Tooltip>
    )
}

/**
 * What a plan includes, grouped per channel so WhatsApp candidates and website chat messages are never mistaken for each
 * other: users and campaigns, then WhatsApp (candidates per month, the price of an extra one), then the website chat.
 * Every plan has the same rows, so they line up side by side; the Website plan includes no candidates and shows the price
 * of each one.
 */
export function usePlanFeatures(): (
    tierPricing: TierPricing,
    overageAmount: number,
    currency: string
) => PlanFeature[] {
    const { t, i18n } = useTranslation()
    const locale = i18n.language.replace('_', '-')

    return (tierPricing, overageAmount, currency) => [
        {
            kind: 'feature',
            content:
                tierPricing.userLimit >= UNLIMITED_USER_THRESHOLD
                    ? t('Unlimited users')
                    : t('{{value}} users', { value: tierPricing.userLimit }),
        },
        { kind: 'feature', content: t('Unlimited campaigns') },
        { kind: 'heading', content: 'WhatsApp' },
        {
            kind: 'feature',
            content: (
                <span>
                    {tierPricing.conversationLimit === 0
                        ? t('{{price}} per candidate', { price: formatPrice(overageAmount, currency) })
                        : t('{{value}} conversations per month', {
                              value: tierPricing.conversationLimit.toLocaleString(locale),
                          })}
                    <Info
                        title={t(
                            'Everyone who comes into a campaign counts as 1 candidate, in the month they come in. If the conversation continues into the next month or you contact them again later, they do not count again. Someone in two campaigns counts as 2.'
                        )}
                    />
                </span>
            ),
        },
        {
            kind: 'note',
            content:
                tierPricing.conversationLimit === 0
                    ? t('No candidates included')
                    : t('Then {{price}} per conversation', { price: formatPrice(overageAmount, currency) }),
        },
        { kind: 'heading', content: t('Website chat') },
        { kind: 'feature', content: t('{{count}} websites', { count: tierPricing.siteLimit }) },
        {
            kind: 'feature',
            content: (
                <span>
                    {t('{{messages}} chat messages from visitors per month', {
                        messages: tierPricing.messageLimit.toLocaleString(locale),
                    })}
                    <Info
                        title={t(
                            'Every message a visitor sends in the chat counts as 1. The answers from Evaa are included.'
                        )}
                    />
                </span>
            ),
        },
    ]
}

export function PlanFeatureRow({ feature, className }: { feature: PlanFeature; className?: string }) {
    if (feature.kind === 'empty') {
        return <div className={className} />
    }

    if (feature.kind === 'heading') {
        return (
            <Typography.Text
                type="secondary"
                className={clsx('mt-1 text-xs font-medium uppercase tracking-wide', className)}
            >
                {feature.content}
            </Typography.Text>
        )
    }

    if (feature.kind === 'note') {
        return (
            <Typography.Text
                type="secondary"
                className={clsx('-mt-1 pl-7 text-sm', className)}
            >
                {feature.content}
            </Typography.Text>
        )
    }

    const missing = feature.kind === 'missing'

    return (
        <Flex
            gap="small"
            align="center"
            className={clsx('w-full', className)}
        >
            <span
                className={clsx('flex h-5 w-5 shrink-0 items-center justify-center rounded-full', {
                    'bg-primary/10 text-ink-wash': !missing,
                    'bg-fill-subtle text-ink-tertiary': missing,
                })}
            >
                {missing ? <MinusOutlined className="text-[10px]" /> : <CheckOutlined className="text-[10px]" />}
            </span>
            <Typography.Text type={missing ? 'secondary' : undefined}>{feature.content}</Typography.Text>
        </Flex>
    )
}
